import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Log } from '@s8l/client-tree-lib';
import { ChartDataSets, ChartOptions } from 'chart.js';
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import { Color, Label } from 'ng2-charts';
import { Subject } from 'rxjs';

import { AnalyticsMetric, MetricAttrMap, StatsService } from 'src/app/services/stats.service';

import { AggregationGroup } from '../../../services/jsonrpc/stats-server.service';

@Component({
  selector: 'app-analytics-graph',
  templateUrl: './analytics-graph.component.html',
  styleUrls: ['./analytics-graph.component.scss']
})
export class AnalyticsGraph implements OnInit, OnDestroy {
  @Input() metric: AnalyticsMetric;
  @Input() filter: { [index in AggregationGroup]: any };
  @Input() exclude: { [index in AggregationGroup]: any };
  @Input() type: string;

  @Input() from: Date;
  @Input() to: Date;
  @Input() relative: boolean;
  @Input() labelNames: string;
  @Input() labelLength = 20;

  @Input() defaultDatasets: AggregationGroup | AggregationGroup[];
  @Input() defaultGrouping: AggregationGroup | AggregationGroup[];
  @Input() excludeGrouping: AggregationGroup[];

  @Input() customGrouping: boolean;

  @Input() reload: Subject<boolean>;

  public loaded = false;
  public error = false;

  public groupBy: AggregationGroup[] = [];
  public datasets: AggregationGroup[] = [];

  public data: ChartDataSets[];
  public labels: Label[];
  public legend = true;
  public options: ChartOptions = {
    responsive: true,
    scales: {
      xAxes: [{ stacked: true }],
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            suggestedMin: 0,
            min: 0,
            callback: (val, i?, values?) => this.stats.formatMetric(this.metric, val)
          }
        }
      ]
    },
    tooltips: {
      callbacks: {
        label: (item, data) => {
          const label = data.datasets[item.datasetIndex].label || '';
          return `${label}: ${this.stats.formatMetric(this.metric, item.yLabel, false)}`;
        }
      }
    }
  };
  public colors: Color[] = [];
  public palette = [
    '#546A76',
    '#73C1C6',
    '#17BEBB',
    '#84DCC6',
    '#84DD63',
    '#23CE6B',
    '#00A676',
    '#800000',
    '#8E443D',
    '#CA3C25',
    '#F64740',
    '#E76F51',
    '#DA667B',
    '#CF8BA3',
    '#9B6A6C',
    '#CF995F',
    '#DBAD6A',
    '#BA1200'
  ];

  public dropdownList = [];
  public selectedItems = [];
  public dropdownSettings: IDropdownSettings = {
    singleSelection: false,
    idField: 'item_id',
    textField: 'item_text',
    enableCheckAll: false,
    itemsShowLimit: 5,
    allowSearchFilter: true
  };

  constructor(private stats: StatsService) {}

  public ngOnInit(): Promise<any> {
    if (this.labelLength > 0) {
      const ll = this.labelLength;
      this.options.scales.xAxes[0].ticks = {
        callback: function (value) {
          const str = value as string;
          return str && str.length > ll ? str.substring(0, Math.max(3, ll - 3)) + '...' : value;
        }
      };
    }

    this.options.scales.yAxes[0].stacked = this.type === 'bar';

    this.reload?.subscribe(all => {
      if (all) this.init();
      else this.initGraph();
    });

    return this.init();
  }

  public ngOnDestroy(): void {
    this.reload?.unsubscribe();
  }

  private init(): Promise<any> {
    if (this.defaultGrouping) {
      if (Array.isArray(this.defaultGrouping)) {
        this.groupBy = this.defaultGrouping;
      } else {
        this.groupBy.push(this.defaultGrouping);
      }
    } else {
      // this.groupBy.push(AggregationGroup.type);
    }

    if (this.defaultDatasets) {
      if (Array.isArray(this.defaultDatasets)) {
        this.datasets = this.defaultDatasets;
      } else {
        this.datasets.push(this.defaultDatasets);
      }
    }

    if (this.customGrouping) {
      for (const group of Object.values(AggregationGroup)) {
        if (this.excludeGrouping && this.excludeGrouping.find(x => x === group)) {
          continue;
        }
        this.dropdownList.push({ item_id: this.dropdownList.length + 1, item_text: group });
      }
      for (const group of this.groupBy) {
        const item = this.dropdownList.find(({ item_text }) => item_text === group);
        if (item) {
          this.selectedItems.push(item);
        }
      }
    }

    return this.initGraph();
  }

  private initGraph(): Promise<any> {
    return this.stats
      .fetchMetrics(this.metric, this.relative, [...this.groupBy, ...this.datasets], this.filter, this.exclude, this.from, this.to)
      .then(data => this.stats.filterNullColumn(data, this.datasets[0]))
      .then(data => this.initData(data, MetricAttrMap[this.metric]))
      .catch(err => {
        this.error = true;
        this.data = null;
        Log.log('AnalyticsGraph', 'Error', err);
      });
  }

  private initData(result, metricName) {
    const data = this.stats.processMetrics(result, this.relative ? 'relative' : metricName, this.groupBy, this.datasets, this.labelNames);

    this.labels = data.columns;
    this.data = data.rows.sort().map(metric => {
      return {
        data: data.columns.map(v => data.values[v][metric] || 0),
        label: metric,
        fill: false
      };
    });

    for (let i = 0; i < data.rows.length; i++) {
      this.colors.push({
        backgroundColor: this.palette[i % this.palette.length],
        borderColor: this.palette[i % this.palette.length],
        pointBorderColor: 'black'
      });
    }
    this.error = false;
    this.loaded = true;
  }

  public onItemSelected(e) {
    this.groupBy.push(e.item_text);
    this.initGraph();
  }

  public onItemDeselected(e) {
    const index = this.groupBy.findIndex(x => x === e.item_text);
    this.groupBy.splice(index, 1);
    if (this.groupBy.length >= 1) {
      this.initGraph();
    }
  }
}
